import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { CsvAttachmentTable } from './CsvAttachmentTable';
import { useCsvFromUrl } from './useCsvFromUrl';

const messages = defineMessages({
  loading: {
    id: 'phc-web-toolkit.subjectViewer.attachments.csvAttachmentViewer.loading',
    defaultMessage: 'Loading CSV…',
  },
  loadError: {
    id: 'phc-web-toolkit.subjectViewer.attachments.csvAttachmentViewer.loadError',
    defaultMessage: 'Could not load this CSV file.',
  },
  bytesTruncated: {
    id: 'phc-web-toolkit.subjectViewer.attachments.csvAttachmentViewer.bytesTruncated',
    defaultMessage:
      'This file is too large to fully preview in the browser. Showing the first portion.',
  },
  download: {
    id: 'phc-web-toolkit.subjectViewer.attachments.csvAttachmentViewer.download',
    defaultMessage: 'Download',
  },
});

const useStyles = makeStyles(theme => ({
  modalContent: {
    padding: 0,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    minHeight: 0,
    minWidth: 0,
    height: '100%',
    width: '100%',
  },
  header: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
  },
  body: {
    display: 'flex',
    flex: 1,
    maxWidth: '100%',
    minHeight: 0,
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'auto',
    padding: theme.spacing(1, 2, 2, 2),
  },
  loader: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    minHeight: 240,
  },
  error: {
    color: theme.palette.error.main,
  },
  notice: {
    color: theme.palette.text.hint,
    padding: theme.spacing(0.5, 0),
  },
  title: {
    fontWeight: 600,
  },
  downloadLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5, 1.5),
    fontSize: theme.typography.button.fontSize,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
  },
}));

export type CsvAttachmentViewerProps = {
  /** Pre-signed URL pointing to the CSV file. */
  url?: string;
  /** Display name shown in the header. */
  fileName: string;
  /** Optional URL used by the "Download" affordance (defaults to `url`). */
  downloadUrl?: string;
  /** When true, hide the chrome (header, download button) — used for inline embeds. */
  hideHeader?: boolean;
  /** Test id for the wrapper. */
  'data-testid'?: string;
};

/**
 * Loads a CSV from a (pre-signed) URL and renders it as a table. Use directly
 * for in-line embeds; wrap in {@link CsvAttachmentModal} for the popup variant.
 */
export const CsvAttachmentViewer: React.FC<CsvAttachmentViewerProps> = ({
  url,
  fileName,
  downloadUrl,
  hideHeader,
  'data-testid': testId,
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const state = useCsvFromUrl({ url });

  const renderBody = () => {
    if (state.status === 'loading' || state.status === 'idle') {
      return (
        <Box className={classes.loader} data-testid={`${testId}-loading`}>
          <DotLoader size={1} />
        </Box>
      );
    }
    if (state.status === 'error') {
      return (
        <Box
          className={classes.body}
          data-testid={`${testId}-error`}
          direction="column"
        >
          <Text className={classes.error}>
            {formatMessage(messages.loadError)}
          </Text>
        </Box>
      );
    }
    return (
      <Box className={classes.body} direction="column" gap={1}>
        {state.truncated && (
          <Text className={classes.notice} size="caption">
            {formatMessage(messages.bytesTruncated)}
          </Text>
        )}
        <CsvAttachmentTable
          csvText={state.csvText}
          data-testid={`${testId}-table`}
        />
      </Box>
    );
  };

  return (
    <Box className={classes.container} data-testid={testId}>
      {!hideHeader && (
        <Box className={classes.header}>
          <Text className={classes.title}>{fileName}</Text>
          {downloadUrl && (
            <a
              className={classes.downloadLink}
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              download
              data-testid={`${testId}-download`}
            >
              {formatMessage(messages.download)}
            </a>
          )}
        </Box>
      )}
      {renderBody()}
    </Box>
  );
};

export type CsvAttachmentModalProps = CsvAttachmentViewerProps & {
  isOpen: boolean;
  onDismiss: () => void;
};

const useModalShellStyles = makeStyles(() => ({
  modalContent: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
  },
}));

/** Modal wrapper for {@link CsvAttachmentViewer}. */
export const CsvAttachmentModal: React.FC<CsvAttachmentModalProps> = ({
  isOpen,
  onDismiss,
  ...viewerProps
}) => {
  const classes = useModalShellStyles();
  return (
    <Modal
      isFullScreen
      isOpen={isOpen}
      onDismiss={onDismiss}
      title={viewerProps.fileName}
      contentClassName={classes.modalContent}
      data-testid={`${
        viewerProps['data-testid'] ?? 'csv-attachment-modal'
      }-modal`}
    >
      <CsvAttachmentViewer {...viewerProps} hideHeader />
    </Modal>
  );
};
